import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { LGoButton, LGoFormField } from "@/ui/branded";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  closesSaleAfterStart,
  saleCloseFromParts,
  saleCloseParts,
  type SaleCloseDirection,
  type SaleCloseUnit,
} from "@/domain/activity/rules";
import type { SaleClose, SaleEndAnchor } from "@/domain/activity/types";
import type { ActivityFormData } from "@/features/activities/form-schema";
import {
  saleCloseCopy,
  saleClosePresets,
  saleCloseUnitLabel,
} from "@/features/activities/sale-close-copy";

interface Draft {
  amount: string;
  unit: SaleCloseUnit;
  direction: SaleCloseDirection;
  anchor: SaleEndAnchor;
}

const blankDraft: Draft = { amount: "", unit: "minutes", direction: "before", anchor: "start" };

function draftFromClose(close: SaleClose): Draft {
  const parts = saleCloseParts(close);
  return { ...parts, amount: String(parts.amount) };
}

type DraftReading =
  | { kind: "blank" }
  | { kind: "invalid" }
  | { kind: "close"; close: SaleClose };

function readDraft(draft: Draft): DraftReading {
  if (draft.amount.trim() === "") return { kind: "blank" };
  if (!/^\d+$/.test(draft.amount.trim())) return { kind: "invalid" };
  return {
    kind: "close",
    close: saleCloseFromParts({ ...draft, amount: Number(draft.amount) }),
  };
}

function isSameClose(a: SaleClose, b: SaleClose): boolean {
  return a.offsetMinutes === b.offsetMinutes && a.anchor === b.anchor;
}

const selectionOptions = { shouldDirty: true } as const;

interface SaleCloseFieldProps {
  disabled: boolean;
}

export function SaleCloseField({ disabled }: SaleCloseFieldProps) {
  const { control, setValue, trigger, formState } = useFormContext<ActivityFormData>();
  const [offsetMinutes, anchor, requiresConfirmation] = useWatch({
    control,
    name: ["sale_end_offset_minutes", "sale_end_anchor", "requires_confirmation"],
  });
  const [draft, setDraft] = useState<Draft>(blankDraft);
  const [hasAskedForCustom, setHasAskedForCustom] = useState(false);

  const chosen: SaleClose | null =
    offsetMinutes !== undefined && Number.isFinite(offsetMinutes) && anchor !== undefined
      ? { offsetMinutes, anchor }
      : null;
  const isChosenAPreset =
    chosen !== null && saleClosePresets.some((preset) => isSameClose(preset.close, chosen));
  const isCustomOpen = hasAskedForCustom || (chosen !== null && !isChosenAPreset);

  useEffect(() => {
    if (offsetMinutes === undefined || !Number.isFinite(offsetMinutes) || anchor === undefined) {
      return;
    }
    const stored = { offsetMinutes, anchor };
    setDraft((current) => {
      const reading = readDraft(current);
      return reading.kind === "close" && isSameClose(reading.close, stored)
        ? current
        : draftFromClose(stored);
    });
  }, [offsetMinutes, anchor]);

  const isReasonAlreadyAnError =
    formState.errors.sale_end_anchor?.message === saleCloseCopy.afterStartRefused;
  const isRefusalNoteShown = requiresConfirmation && !isReasonAlreadyAnError;
  const isRefused = (close: SaleClose) => requiresConfirmation && closesSaleAfterStart(close);

  const store = (nextOffset: number | undefined, nextAnchor: SaleEndAnchor | undefined) => {
    setValue("sale_end_offset_minutes", nextOffset, selectionOptions);
    setValue("sale_end_anchor", nextAnchor, selectionOptions);
    void trigger(["sale_end_offset_minutes", "sale_end_anchor"]);
  };

  const choosePreset = (close: SaleClose) => {
    setHasAskedForCustom(false);
    if (!isCustomOpen && chosen !== null && isSameClose(chosen, close)) {
      store(undefined, undefined);
      return;
    }
    store(close.offsetMinutes, close.anchor);
  };

  const toggleCustom = () => {
    if (isCustomOpen) {
      setHasAskedForCustom(false);
      setDraft(blankDraft);
      store(undefined, undefined);
      return;
    }
    setHasAskedForCustom(true);
  };

  const editDraft = (changes: Partial<Draft>) => {
    const next = { ...draft, ...changes };
    setDraft(next);
    setHasAskedForCustom(true);
    const reading = readDraft(next);
    if (reading.kind === "close") store(reading.close.offsetMinutes, reading.close.anchor);
    else if (reading.kind === "invalid") store(Number.NaN, next.anchor);
    else store(undefined, undefined);
  };

  return (
    <LGoFormField name="sale_end_anchor" label={saleCloseCopy.label} hint={saleCloseCopy.hint}>
      {() => (
        <div className="space-y-3">
          <div
            role="group"
            aria-label={saleCloseCopy.label}
            aria-describedby={isRefusalNoteShown ? "sale-close-refusal" : undefined}
            className="flex flex-wrap gap-2"
          >
            {saleClosePresets.map(({ label, close }) => {
              const isSelected = !isCustomOpen && chosen !== null && isSameClose(chosen, close);
              return (
                <LGoButton
                  key={label}
                  type="button"
                  size="sm"
                  className="rounded-full"
                  variant={isSelected ? "primary" : "secondary"}
                  aria-pressed={isSelected}
                  disabled={disabled || (isRefused(close) && !isSelected)}
                  onClick={() => choosePreset(close)}
                >
                  {label}
                </LGoButton>
              );
            })}
            <LGoButton
              type="button"
              size="sm"
              className="rounded-full"
              variant={isCustomOpen ? "primary" : "secondary"}
              aria-pressed={isCustomOpen}
              disabled={disabled}
              onClick={toggleCustom}
            >
              {saleCloseCopy.customChoice}
            </LGoButton>
          </div>

          {isRefusalNoteShown && (
            <p id="sale-close-refusal" className="text-xs text-muted-foreground">
              {saleCloseCopy.afterStartRefused}
            </p>
          )}

          {isCustomOpen && (
            <SaleCloseSentence
              draft={draft}
              disabled={disabled}
              isAfterStartRefused={requiresConfirmation}
              onChange={editDraft}
              offsetError={formState.errors.sale_end_offset_minutes?.message}
            />
          )}
        </div>
      )}
    </LGoFormField>
  );
}

interface SaleCloseSentenceProps {
  draft: Draft;
  disabled: boolean;
  isAfterStartRefused: boolean;
  offsetError: string | undefined;
  onChange: (changes: Partial<Draft>) => void;
}

function SaleCloseSentence({
  draft,
  disabled,
  isAfterStartRefused,
  offsetError,
  onChange,
}: SaleCloseSentenceProps) {
  const amount = Number(draft.amount);
  const isCloseCappedAtTerminEnd =
    (draft.anchor === "end" || draft.direction === "after") &&
    offsetError !== saleCloseCopy.afterEndRefused;
  return (
    <div data-field="sale_end_offset_minutes" className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-sm text-foreground">
        <span>{saleCloseCopy.sentenceLead}</span>
        <Input
          type="number"
          min={0}
          inputMode="numeric"
          aria-label={saleCloseCopy.amountLabel}
          className="w-20"
          value={draft.amount}
          disabled={disabled}
          onChange={(event) => onChange({ amount: event.target.value })}
        />
        <Select
          value={draft.unit}
          onValueChange={(unit) => onChange({ unit: unit as SaleCloseUnit })}
          disabled={disabled}
        >
          <SelectTrigger aria-label={saleCloseCopy.unitLabel} className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="minutes">{saleCloseUnitLabel("minutes", amount)}</SelectItem>
            <SelectItem value="hours">{saleCloseUnitLabel("hours", amount)}</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={draft.direction}
          onValueChange={(direction) => onChange({ direction: direction as SaleCloseDirection })}
          disabled={disabled}
        >
          <SelectTrigger aria-label={saleCloseCopy.directionLabel} className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="before">{saleCloseCopy.directions.before}</SelectItem>
            <SelectItem value="after" disabled={isAfterStartRefused || draft.anchor === "end"}>
              {saleCloseCopy.directions.after}
            </SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={draft.anchor}
          onValueChange={(anchor) => onChange({ anchor: anchor as SaleEndAnchor })}
          disabled={disabled}
        >
          <SelectTrigger aria-label={saleCloseCopy.anchorLabel} className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="start">{saleCloseCopy.anchors[draft.direction].start}</SelectItem>
            <SelectItem value="end" disabled={isAfterStartRefused || draft.direction === "after"}>
              {saleCloseCopy.anchors[draft.direction].end}
            </SelectItem>
          </SelectContent>
        </Select>
        <span>{saleCloseCopy.sentenceTail}</span>
      </div>
      {isCloseCappedAtTerminEnd && (
        <p className="text-xs text-muted-foreground">{saleCloseCopy.afterEndRefused}</p>
      )}
      {offsetError && <p className="text-xs text-destructive">{offsetError}</p>}
    </div>
  );
}
